import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { LineReveal } from "./Reveal";
import { GUIDE_IMG } from "../constants/site";
import { scrollToId } from "../hooks/useLenis";

const STATS = [
  { v: "+15", l: "ans d'expertise" },
  { v: "500+", l: "clients accompagnés" },
  { v: "5", l: "univers produits" },
];

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 90, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 90, damping: 18 });
  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 800], [0, 120]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="grain relative min-h-screen overflow-hidden bg-navy text-white"
    >
      <div className="pointer-events-none absolute -right-10 top-24 select-none font-display text-[38vw] font-extrabold leading-none text-white/[0.03]">IBM</div>
      <div className="pointer-events-none absolute left-0 top-0 h-full w-px bg-white/5 lg:left-[8.333%]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-px w-full bg-white/10" />

      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-32 sm:px-8 lg:grid-cols-12 lg:px-16 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="eyebrow text-gold">
            Matériel informatique · Logiciels de gestion · Dakar
          </motion.p>
          <LineReveal
            className="mt-8 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl"
            delay={0.35}
            lines={[
              <>Le matériel</>,
              <>qui <span className="font-serif italic font-medium text-gold">travaille</span>,</>,
              <>le logiciel</>,
              <>qui <span className="font-serif italic font-medium text-gold">gère</span>.</>,
            ]}
          />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-8 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            IBM Ibn Bachir Multimédia équipe les entreprises et les particuliers : ordinateurs, imprimantes, réseau, consommables — et développe des logiciels de gestion sur mesure pour piloter votre activité.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-cta-catalogue" onClick={() => scrollToId("catalogue")} className="btn-white group">
              Découvrir le catalogue <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-cta-logiciel" onClick={() => scrollToId("contact")} className="btn-ghost">
              Demander un logiciel
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="mt-16 grid max-w-lg grid-cols-3 divide-x divide-white/10 border-t border-white/10 pt-6">
            {STATS.map((s) => (
              <div key={s.l} className="px-4 first:pl-0" data-testid={`hero-stat-${s.v}`}>
                <div className="font-display text-2xl font-bold text-white sm:text-3xl">{s.v}</div>
                <div className="mt-1 text-xs text-white/50">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rotX, rotateY: rotY, y: parallax }} className="relative mx-auto w-full max-w-md" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}>
            <div className="absolute -inset-3 clip-frame border border-gold/60" style={{ transform: "translateZ(-30px)" }} />
            <div className="absolute -right-6 -top-6 h-24 w-24 border-r border-t border-gold" />
            <div className="absolute -bottom-6 -left-6 h-24 w-24 border-b border-l border-gold" />
            <div className="clip-frame relative aspect-[4/5] overflow-hidden bg-royal">
              <img src={GUIDE_IMG} alt="Notre guide — Ibn Bachir Multimédia" data-testid="hero-guide-image" className="h-full w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="eyebrow text-gold">Notre guide</p>
                <p className="mt-1 font-serif text-lg italic text-white">« Servir avec rigueur, équiper avec confiance. »</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button style={{ opacity: fade }} onClick={() => scrollToId("catalogue")} data-testid="hero-scroll-indicator" className="absolute bottom-8 left-4 z-10 hidden items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/50 sm:left-8 lg:left-16 lg:flex">
        <ArrowDown className="h-4 w-4 animate-bounce" /> Défiler
      </motion.button>
    </section>
  );
};
